import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LoginScreen } from "@/features/auth/LoginScreen";
import { OverviewScreen } from "@/features/overview/OverviewScreen";
import { CustomersScreen } from "@/features/customers/CustomersScreen";
import { RenewalsScreen } from "@/features/renewals/RenewalsScreen";
import { SettingsScreen } from "@/features/settings/SettingsScreen";
import { ImportScreen } from "@/features/import/ImportScreen";
import { NotificationsScreen } from "@/features/notifications/NotificationsScreen";
import { CatalogueScreen } from "@/features/catalogue/CatalogueScreen";
import { Skeleton } from "@/shared/ui/Skeleton";
import { useAuthStore } from "@/store/authStore";
import { AppShell } from "./AppShell";
import { ProtectedRoute } from "./ProtectedRoute";

// Lazy chunk: keeps recharts out of the main bundle.
const MetricsScreen = lazy(() =>
  import("@/features/metrics/MetricsScreen").then((m) => ({ default: m.MetricsScreen })),
);

function PageSkeleton() {
  return <Skeleton className="h-[320px] w-full" />;
}

export function App() {
  const bootstrap = useAuthStore((s) => s.bootstrap);

  // On boot, re-validate any stored tokens (auto-login on hard refresh).
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route path="/overview" element={<OverviewScreen />} />
            <Route
              path="/metrics"
              element={
                <Suspense fallback={<PageSkeleton />}>
                  <MetricsScreen />
                </Suspense>
              }
            />
            <Route path="/customers" element={<CustomersScreen />} />
            <Route path="/renewals" element={<RenewalsScreen />} />
            <Route path="/settings" element={<SettingsScreen />} />
            <Route path="/import" element={<ImportScreen />} />
            <Route path="/notifications" element={<NotificationsScreen />} />
            <Route path="/catalogue" element={<CatalogueScreen />} />
          </Route>
        </Route>
        {/* Default + catch-all -> overview (the default landing page). */}
        <Route path="/" element={<Navigate to="/overview" replace />} />
        <Route path="*" element={<Navigate to="/overview" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
